/**
 * Ohm's law and the power law: V = I × R and P = V × I, solved for any two knowns.
 *
 * All values are SI base units (volts, amperes, ohms, watts). Direct current and
 * purely resistive loads only: with reactance in the circuit V/I is impedance, not R.
 */

export type OhmVariable = "V" | "I" | "R" | "P";

export interface OhmValues {
  V: number;
  I: number;
  R: number;
  P: number;
}

export type OhmError = "need-positive" | "need-two";

export type OhmResult =
  | { ok: true; values: OhmValues; formulas: Record<OhmVariable, OhmFormula[]> }
  | { ok: false; error: OhmError };

/** One of the twelve "power wheel" formulas: how a quantity follows from two others. */
export interface OhmFormula {
  target: OhmVariable;
  /** Text such as "V = I × R". */
  text: string;
  /** The two quantities the formula needs. */
  uses: [OhmVariable, OhmVariable];
  value: number;
}

export const OHM_VARIABLES: readonly OhmVariable[] = ["V", "I", "R", "P"];

/** Every unordered pair of knowns, in the order the pair menu shows them. */
export const OHM_PAIRS: readonly [OhmVariable, OhmVariable][] = [
  ["V", "I"],
  ["V", "R"],
  ["I", "R"],
  ["V", "P"],
  ["I", "P"],
  ["R", "P"],
];

/** Standard resistor power ratings in watts, smallest first. */
export const STANDARD_WATTAGES: readonly number[] = [
  0.125, 0.25, 0.5, 1, 2, 3, 5, 10, 25, 50, 100,
];

type Pair = readonly [OhmVariable, OhmVariable];

function samePair(a: Pair, b: Pair): boolean {
  return (a[0] === b[0] && a[1] === b[1]) || (a[0] === b[1] && a[1] === b[0]);
}

function solveFromPair(
  pair: Pair,
  known: Partial<Record<OhmVariable, number>>
): OhmValues | null {
  const get = (key: OhmVariable): number => known[key] ?? Number.NaN;
  if (samePair(pair, ["V", "I"])) {
    const [V, I] = [get("V"), get("I")];
    return I > 0 ? { V, I, R: V / I, P: V * I } : null;
  }
  if (samePair(pair, ["V", "R"])) {
    const [V, R] = [get("V"), get("R")];
    return { V, R, I: V / R, P: (V * V) / R };
  }
  if (samePair(pair, ["I", "R"])) {
    const [I, R] = [get("I"), get("R")];
    return { I, R, V: I * R, P: I * I * R };
  }
  if (samePair(pair, ["V", "P"])) {
    const [V, P] = [get("V"), get("P")];
    return V > 0 ? { V, P, I: P / V, R: (V * V) / P } : null;
  }
  if (samePair(pair, ["I", "P"])) {
    const [I, P] = [get("I"), get("P")];
    return I > 0 ? { I, P, V: P / I, R: P / (I * I) } : null;
  }
  const [R, P] = [get("R"), get("P")];
  return { R, P, V: Math.sqrt(P * R), I: Math.sqrt(P / R) };
}

/**
 * The three ways to reach each quantity, evaluated from the solved set — the "power
 * wheel". All three agree by construction, which is the check a student wants to see.
 */
export function powerWheel(values: OhmValues): OhmFormula[] {
  const { V, I, R, P } = values;
  return [
    { target: "V", text: "V = I × R", uses: ["I", "R"], value: I * R },
    { target: "V", text: "V = P ÷ I", uses: ["P", "I"], value: P / I },
    {
      target: "V",
      text: "V = √(P × R)",
      uses: ["P", "R"],
      value: Math.sqrt(P * R),
    },
    { target: "I", text: "I = V ÷ R", uses: ["V", "R"], value: V / R },
    { target: "I", text: "I = P ÷ V", uses: ["P", "V"], value: P / V },
    {
      target: "I",
      text: "I = √(P ÷ R)",
      uses: ["P", "R"],
      value: Math.sqrt(P / R),
    },
    { target: "R", text: "R = V ÷ I", uses: ["V", "I"], value: V / I },
    { target: "R", text: "R = V² ÷ P", uses: ["V", "P"], value: (V * V) / P },
    { target: "R", text: "R = P ÷ I²", uses: ["P", "I"], value: P / (I * I) },
    { target: "P", text: "P = V × I", uses: ["V", "I"], value: V * I },
    { target: "P", text: "P = I² × R", uses: ["I", "R"], value: I * I * R },
    { target: "P", text: "P = V² ÷ R", uses: ["V", "R"], value: (V * V) / R },
  ];
}

/**
 * Solve from two knowns. R must be above zero when given, and any known that the other
 * unknowns are divided by (I for V&I and I&P, V for V&P) must be above zero too.
 */
export function solveOhm(
  first: OhmVariable,
  second: OhmVariable,
  known: Partial<Record<OhmVariable, number>>
): OhmResult {
  const a = known[first];
  const b = known[second];
  if (a === undefined || b === undefined) {
    return { ok: false, error: "need-two" };
  }
  const negative = a < 0 || b < 0;
  const zeroResistorOrPower =
    (first === "R" && a === 0) ||
    (second === "R" && b === 0) ||
    (first === "P" && a === 0 && (second === "R" || second === "V")) ||
    (second === "P" && b === 0 && (first === "R" || first === "V"));
  if (negative || zeroResistorOrPower) {
    return { ok: false, error: "need-positive" };
  }
  const values = solveFromPair([first, second], known);
  if (values === null) {
    return { ok: false, error: "need-positive" };
  }
  const wheel = powerWheel(values);
  const formulas: Record<OhmVariable, OhmFormula[]> = {
    V: [],
    I: [],
    R: [],
    P: [],
  };
  for (const formula of wheel) {
    formulas[formula.target].push(formula);
  }
  return { ok: true, values, formulas };
}

/** The smallest standard rating at or above twice the dissipated power, or null. */
export function suggestedWattage(power: number): number | null {
  if (!(power > 0)) {
    return null;
  }
  return STANDARD_WATTAGES.find((rating) => rating >= 2 * power) ?? null;
}
